import React, {MutableRefObject, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useAuth} from '../contexts/auth/context';

const DEFAULT_PAGE_SIZE: number = 8;

export type ScrollInstance<T> = {
  data: T[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  isLoading: boolean;
  pagesCount: number;
  currentPage: number;
  hasNextPage: boolean;
  triggerRefetch: () => void;
  handleGetNextPage: () => void;
  handleApplyFilters: (filtersString?: string) => void;
  handleApplySort: (sortString?: string) => void;
  handleUpdateCurrentSearch: (search?: string) => void;
  currentSearch?: string;
  currentSort?: string;
  currentFilters?: string;
  counter?: number;
  firstLoad: boolean;
  error?: string;
  enabled?: boolean;
};

export type ScrollFetchParams = {
  limit: number;
  skip: number;
  search?: string;
  filters?: string;
  sort?: string;
};

interface InfinityScrollItemsParams<T> {
  fetchData: (params: ScrollFetchParams) => Promise<{
    totalCount: number;
    data: T[];
  }>;
  pageSize: number;
  loaderRef: MutableRefObject<HTMLSpanElement> | MutableRefObject<HTMLElement> | undefined;
  updateUrl?: boolean;
  onMount?: boolean;
  initialSort?: string;
  dataTransformer?: (data: T[]) => void;
  enabled?: boolean;
}

export function useInfinityScrollItems<T>({
  fetchData,
  pageSize = DEFAULT_PAGE_SIZE,
  loaderRef,
  updateUrl = true,
  onMount = true,
  initialSort = '',
  dataTransformer,
  enabled = true,
}: InfinityScrollItemsParams<T>): ScrollInstance<T> {
  const authContext = useAuth();
  const [data, setData] = useState<T[]>([]);
  const [counter, setCounter] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [pagesCount, setPagesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSearch, setCurrentSearch] = useState<string | undefined>('');
  const [currentFilters, setCurrentFilters] = useState<string | undefined>('');
  const [currentSort, setCurrentSort] = useState<string | undefined>(initialSort);
  const [isMounted, setIsMounted] = useState(false);
  const [firstLoad, setFirstLoad] = useState(false);

  const failedSecurityClearence = useMemo(
    () =>
      authContext.state.session?.affiliations &&
      authContext.state.session?.affiliations.length > 0 &&
      !authContext.state.session?.selectedAffiliation,
    [authContext]
  );

  let history = useHistory();

  let {url} = useRouteMatch();

  const hasNextPage = useMemo(() => currentPage < pagesCount, [currentPage, pagesCount]);

  const handleUpdateUrl = useCallback(() => {
    let queryUrl = url;

    // Define route
    let queryTextParts: string[] = [];

    // Add search params the current path
    if (currentSearch || currentFilters || currentSort) {
      if (currentSearch && currentSearch.trim().length !== 0)
        queryTextParts.push(`search=${currentSearch.trim()}`);

      if (currentFilters && currentFilters.trim().length !== 0) queryTextParts.push(currentFilters);

      if (currentSort && currentSort.trim().length !== 0)
        queryTextParts.push(`sort=${currentSort}`);
    }

    // Concatenate acc params
    queryUrl = `${url}` + (queryTextParts.length > 0 ? `?${queryTextParts.join('&')}` : '');

    // Build new URL and push
    history.replace(queryUrl);
  }, [currentSearch, currentFilters, currentSort, history, url]);

  const handleGetData = useCallback(async () => {
    if (!enabled) return;

    // Set loading
    setIsLoading(true);

    // Security check
    if (failedSecurityClearence) {
      // Log
      console.warn('Current user does not have a selected affiliation so it cannot proceed.');

      // reset
      handleReset();

      // Stop execution here
      return;
    }

    try {
      // Load actual paged data
      const {totalCount, data: pageData}: {totalCount: number; data: T[]} = await fetchData({
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
        search: currentSearch,
        filters: currentFilters,
        sort: currentSort,
      });

      // intercept and transform
      if (dataTransformer) dataTransformer(pageData);

      setCounter(totalCount);

      // Update page data
      if (currentPage === 1) {
        setData(pageData);
      } else {
        setData((state) => [...state, ...pageData]);
      }

      // Update pages count
      setPagesCount(Math.ceil(totalCount / pageSize));

      // Extra state to track if we have a first batch of data
      if (!firstLoad) setFirstLoad(true);

      // Unset previous errors, if any
      setError(undefined);
    } catch (error: any) {
      // Log
      console.error('🔴 Could not get data.', error);

      // Update data
      setData([]);
      setPagesCount(0);
      setCurrentPage(1);

      // Show error
      setError(error.message);
    }

    // Stop loading
    setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedSecurityClearence, currentPage, currentSearch, currentFilters, currentSort, enabled]);

  const getPageData = useCallback(() => {
    if (updateUrl) handleUpdateUrl();
    handleGetData();
  }, [handleGetData, handleUpdateUrl, updateUrl]);

  // External methods for search, filter and sort
  const handleUpdateCurrentSearch = (search?: string) => setCurrentSearch(search);

  const handleApplySort = (encodedSortString?: string) => setCurrentSort(encodedSortString);

  const handleApplyFilters = (encodedFilterString?: string) =>
    setCurrentFilters(encodedFilterString);

  const refreshPage = () => {
    handleReset();
    handleGetData();
  };

  const handleReset = () => {
    setData([]);
    setPagesCount(0);
    setCurrentPage(1);
    setError(undefined);
    setIsLoading(false);
  };

  const handleGetNextPage = () => {
    if (hasNextPage) setCurrentPage((state) => state + 1);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!isLoading && entry.isIntersecting) setCurrentPage((state) => state + 1);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (loaderRef && loaderRef?.current) observer.observe(loaderRef?.current);
  }, [isLoading, loaderRef]);

  useEffect(() => {
    enabled && isMounted && getPageData();
  }, [currentPage, isMounted, handleGetData, handleUpdateUrl, getPageData, enabled]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentSearch, currentFilters, currentSort]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      setFirstLoad(false);
    };
  }, []);

  return {
    data,
    setData,
    isLoading,
    error,
    triggerRefetch: refreshPage,
    handleGetNextPage,
    pagesCount,
    currentPage,
    hasNextPage,
    currentSearch,
    currentSort,
    currentFilters,
    handleApplyFilters,
    handleApplySort,
    handleUpdateCurrentSearch,
    counter,
    firstLoad,
    enabled,
  };
}
